import React, { useState, useMemo } from 'react';

import { Select } from '@eika/select';

import { ConsentAssignment } from '../../components/ConsentAssignment';
import { SigningOrder } from '../../components/SigningOrder';
import { TaskSkeleton } from '../../components/TaskSkeleton';
import { ErrorProvider } from '../../context/ErrorContext';
import { KundeSigneringsordreDTO, Samtykkeoppdrag } from '../../generated-types';
import { useConsentAssignments } from '../../hooks/useConsentAssignments';
import { useSigningOrders } from '../../hooks/useSigningOrders';
import { useTranslation } from '../../translations';
import { ArchiveType } from '../../translations/translationType';
import { findArchivedTasks } from '../../utils/tasksUtils';

import './ArchiveApp.scss';

interface ArchiveAppProps {
    signingOrders: KundeSigneringsordreDTO[];
    currentUserFnr: string;
    consentAssignments: Samtykkeoppdrag[];
    sortOrder: string;
}

const selectValues = (archive: ArchiveType) => [
    {
        key: 'newest',
        text: archive.newestFirst,
        value: 'newest',
    },
    {
        key: 'oldest',
        text: archive.oldestFirst,
        value: 'oldest',
    },
];
const ArchiveApp = ({ signingOrders, currentUserFnr, consentAssignments, sortOrder }: ArchiveAppProps) => {
    const { archive } = useTranslation();

    const newestFirst = sortOrder === 'newest';
    const archivedTasks = useMemo(
        () =>
            signingOrders
                .filter(findArchivedTasks)
                .sort((signingOrderA, signingOrderB) =>
                    newestFirst
                        ? signingOrderB.statusTidspunkt - signingOrderA.statusTidspunkt
                        : signingOrderA.statusTidspunkt - signingOrderB.statusTidspunkt,
                ),
        [signingOrders, newestFirst],
    );
    const completedConsentAssignments = consentAssignments.filter(
        (consentAssignment) =>
            consentAssignment.status === 'ACCEPTED' ||
            consentAssignment.status === 'REJECTED' ||
            consentAssignment.status === 'EXPIRED',
    );
    const hasTasks = archivedTasks.length > 0 || completedConsentAssignments.length > 0;
    return (
        <>
            {hasTasks ? (
                <>
                    {archivedTasks.length > 0 &&
                        archivedTasks.map((signingOrder) => (
                            <SigningOrder
                                key={signingOrder.id}
                                signingOrder={signingOrder}
                                currentUserFnr={currentUserFnr}
                            />
                        ))}
                    {completedConsentAssignments.length > 0 &&
                        completedConsentAssignments.map((completedConsentAssignment) => (
                            <ConsentAssignment
                                key={completedConsentAssignment.sakUuid}
                                consentAssignmentTask={completedConsentAssignment}
                            />
                        ))}
                </>
            ) : (
                <span>{archive.noArchivedTasks}</span>
            )}
        </>
    );
};

export default () => {
    const { isLoading: signingOrdersLoading, data: signingOrders } = useSigningOrders(true);
    const { isLoading: consentAssignmentsLoading, data: consentAssignments } = useConsentAssignments();
    const { archive } = useTranslation();
    const [sortOrder, setSortOrder] = useState<string>(selectValues(archive)[0].value);
    return (
        <ErrorProvider>
            <div className="archive">
                <div className="archive__header-wrapper">
                    <h1 className="archive__title">{archive.title}</h1>
                    <Select
                        className="archive__select"
                        values={selectValues(archive)}
                        defaultValue={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        disabled={signingOrdersLoading || consentAssignmentsLoading}
                    />
                </div>
                {signingOrdersLoading || consentAssignmentsLoading ? (
                    <TaskSkeleton />
                ) : (
                    <ArchiveApp
                        signingOrders={signingOrders?.signeringsordre ?? []}
                        consentAssignments={consentAssignments ?? []}
                        currentUserFnr={signingOrders?.fnr ?? ''}
                        sortOrder={sortOrder}
                    />
                )}
            </div>
        </ErrorProvider>
    );
};
